import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar as CalIcon, RefreshCw, LogOut, Users, BarChart3, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CleanerView } from './CleanerView';
import { MonthNav, StatCard, STATUS_LABELS } from './SharedComponents';

const DAYS_IT = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
const PROP_ORDER = ['Suite', 'Bellavista', 'Oasis', 'Emerald', 'La Crisalide'];
const PROP_COLORS = {
  'Suite': { bg: '#3b82f6', light: '#dbeafe', text: '#1e40af' },
  'Bellavista': { bg: '#22c55e', light: '#dcfce7', text: '#166534' },
  'Oasis': { bg: '#f97316', light: '#ffedd5', text: '#9a3412' },
  'Emerald': { bg: '#10b981', light: '#d1fae5', text: '#065f46' },
  'La Crisalide': { bg: '#eab308', light: '#fef9c3', text: '#854d0e' },
};
const RESPONSABILI = [
  { id: 'enza', name: 'Enza' },
  { id: 'alfredo', name: 'Alfredo' },
  { id: 'daniela', name: 'Daniela' },
  { id: 'citrona', name: 'Citrona' },
  { id: 'talc_cleaning', name: 'Talc Cleaning' },
];
const SUITE_BELLA_ASSIGNEES = ['enza', 'alfredo', 'daniela', 'citrona'];
const getAssignees = (prop) => {
  if (prop === 'Suite' || prop === 'Bellavista') return RESPONSABILI.filter(r => SUITE_BELLA_ASSIGNEES.includes(r.id));
  return RESPONSABILI;
};
// Only these are clearly host-initiated blocks (not real bookings)
const HOST_BLOCK_SUMMARIES = new Set(['airbnb (not available)', 'not available']);
const isBlockEvent = (summary) => HOST_BLOCK_SUMMARIES.has((summary || '').trim().toLowerCase());
const SOURCE_LABELS = { airbnb: 'Airbnb', booking: 'Booking' };

const API = process.env.REACT_APP_BACKEND_URL || '';
const getHeaders = () => {
  const token = sessionStorage.getItem('cleaning_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const CleaningDashboard = ({ user, onLogout }) => {
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedProp, setSelectedProp] = useState('all');
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [view, setView] = useState('calendar');
  const [report, setReport] = useState([]);
  const [exportResp, setExportResp] = useState('all');
  const [reportProp, setReportProp] = useState('all');
  const [guestData, setGuestData] = useState([]);

  const isAdmin = user.roles?.some(r => r.role === 'admin');
  const isManager = user.roles?.some(r => r.role === 'manager');
  const isCleaner = user.roles?.some(r => r.role === 'cleaner') && !isAdmin && !isManager;
  const hasDashboardView = user.roles?.some(r => r.dashboard_view);
  const canSync = isAdmin || user.roles?.some(r => r.can_sync);
  const canAssign = isAdmin || (isManager && !user.roles?.some(r => r.can_assign === false));
  const userReassignTo = user.roles?.find(r => r.reassign_to)?.reassign_to || null;
  const showGuestData = isAdmin || isManager || hasDashboardView;

  const fetchData = useCallback(async () => {
    const headers = getHeaders();
    const pf = selectedProp !== 'all' ? `&property=${encodeURIComponent(selectedProp)}` : '';
    try {
      const [p, b, t, s] = await Promise.all([
        fetch(`${API}/api/cleaning/properties`, { headers, credentials: 'include' }),
        fetch(`${API}/api/cleaning/bookings?year=${year}&month=${month + 1}${pf}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/cleaning/tasks?year=${year}&month=${month + 1}${pf}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/cleaning/sync/status`, { headers, credentials: 'include' }),
      ]);
      if (p.ok) setProperties(await p.json());
      if (b.ok) setBookings(await b.json());
      if (t.ok) setTasks(await t.json());
      if (s.ok) setLastSync(await s.json());
    } catch (err) { /* network error */ }
    if (showGuestData) {
      try {
        const g = await fetch(`${API}/api/cleaning/checkout-guests?year=${year}&month=${month + 1}`, { headers, credentials: 'include' });
        if (g.ok) setGuestData(await g.json());
      } catch (err) { /* non-critical */ }
    }
    if (isAdmin) {
      try { const r = await fetch(`${API}/api/cleaning/users`, { headers, credentials: 'include' }); if (r.ok) setAllUsers(await r.json()); } catch (err) { /* non-critical */ }
    }
  }, [selectedProp, month, year, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try { await fetch(`${API}/api/cleaning/sync`, { method: 'POST', headers: getHeaders(), credentials: 'include' }); await fetchData(); } catch (err) { /* failed */ }
    setSyncing(false);
  };

  const fetchReport = useCallback(async () => {
    const pf = reportProp !== 'all' ? `&property=${encodeURIComponent(reportProp)}` : '';
    try { const r = await fetch(`${API}/api/cleaning/reports/monthly?year=${year}&month=${month + 1}${pf}`, { headers: getHeaders(), credentials: 'include' }); if (r.ok) setReport(await r.json()); } catch (err) { /* failed */ }
  }, [year, month, reportProp]);
  useEffect(() => { if (view === 'report') fetchReport(); }, [view, fetchReport]);

  const updateTask = async (taskId, updates) => {
    await fetch(`${API}/api/cleaning/tasks/${taskId}`, { method: 'PUT', headers: getHeaders(), credentials: 'include', body: JSON.stringify(updates) });
    await fetchData();
  };

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthStr = String(month + 1).padStart(2, '0');

  const visibleProps = useMemo(() => {
    if (selectedProp !== 'all') return PROP_ORDER.filter(n => n === selectedProp);
    return PROP_ORDER.filter(n => properties.some(p => p.name === n));
  }, [properties, selectedProp]);

  // Determine own_tasks_only properties from user roles
  const ownTasksProps = useMemo(() => {
    const props = new Set();
    user.roles?.forEach(r => { if (r.own_tasks_only) r.own_tasks_only.forEach(p => props.add(p)); });
    return props;
  }, [user.roles]);

  // Build ops table with confirmed/not confirmed status and guest data
  const opsTable = useMemo(() => {
    const mStart = `${year}-${monthStr}-01`;
    const nM = month + 2 > 12 ? 1 : month + 2;
    const nY = month + 2 > 12 ? year + 1 : year;
    const mEnd = `${nY}-${String(nM).padStart(2, '0')}-01`;
    const today = new Date();
    today.setHours(0,0,0,0);
    const rows = [];
    const seenKeys = new Set();

    // For dashboard cleaners: only show checkouts where they have a task
    const isDashboardCleaner = isCleaner && hasDashboardView;

    bookings.forEach(b => {
      if (b.end >= mStart && b.end <= mEnd) {
        const t = tasks.find(tk => tk.property === b.property && tk.date === b.end && (!tk.booking_uid || tk.booking_uid === b.uid));

        // Skip if user has own_tasks_only for this property and no matching task
        if (!isAdmin && ownTasksProps.has(b.property) && !t) return;
        // Skip if dashboard cleaner and no matching task for this property
        if (isDashboardCleaner && !t) return;

        const coDate = new Date(b.end + 'T00:00:00');
        const daysUntil = Math.ceil((coDate - today) / 86400000);
        const confirmed = daysUntil <= 14;
        const block = isBlockEvent(b.summary);
        const gm = guestData.find(g => g.property === b.property && g.booking_uid === b.uid);
        const key = `${b.property}_${b.end}`;
        seenKeys.add(key);
        rows.push({
          date: b.end, start: b.start, property: b.property, summary: b.summary, source: b.source,
          assigned: t?.assigned_to || '', status: t?.status || 'da_assegnare', taskId: t?.id, notes: t?.notes || '',
          uid: b.uid, confirmed, daysUntil, isBlock: block,
          isLongBlock: gm?.is_long_block ?? false,
          isHiddenTurnover: false,
          totalGuests: gm?.total_guests ?? null,
          dateMismatch: gm?.date_mismatch ?? null,
        });
      }
    });

    // Add hidden turnovers detected from guest registrations
    guestData.filter(g => g.is_hidden_turnover).forEach(g => {
      const key = `${g.property}_${g.end}`;
      if (!seenKeys.has(key)) {
        const coDate = new Date(g.end + 'T00:00:00');
        const daysUntil = Math.ceil((coDate - today) / 86400000);
        seenKeys.add(key);
        rows.push({
          date: g.end, start: g.start, property: g.property, summary: g.summary, source: g.source,
          assigned: '', status: 'da_assegnare', taskId: null, notes: '',
          uid: g.booking_uid, confirmed: daysUntil <= 14, daysUntil, isBlock: false,
          isLongBlock: false, isHiddenTurnover: true,
          totalGuests: g.total_guests, dateMismatch: null,
        });
      }
    });

    rows.sort((a, b) => a.date.localeCompare(b.date) || PROP_ORDER.indexOf(a.property) - PROP_ORDER.indexOf(b.property));
    return rows;
  }, [bookings, tasks, month, year, monthStr, guestData]);

  const handleExportExcel = () => {
    window.open(`${API}/api/cleaning/export/tasks?year=${year}&month=${month + 1}&responsabile=${exportResp}&token=${sessionStorage.getItem('cleaning_token')}`, '_blank');
  };

  if (isCleaner && !hasDashboardView) return <CleanerView user={user} tasks={tasks} properties={properties} onLogout={onLogout} onUpdateTask={updateTask} />;

  return (
    <div className="min-h-screen bg-[#F7F9F9]" data-testid="cleaning-dashboard">
      {/* Header */}
      <div className="bg-[#3e716c] text-white py-3 px-4 fixed top-0 left-0 right-0 z-30">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex justify-between items-center">
            <h1 className="text-base sm:text-lg font-bold truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Homewise Cleaning</h1>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] sm:text-xs text-white/70 hidden sm:inline">{user.name}</span>
              <Button size="sm" variant="ghost" onClick={onLogout} className="text-white hover:bg-white/20 h-8 w-8 p-0" data-testid="admin-logout-btn"><LogOut size={16} /></Button>
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="flex gap-1 bg-white/10 rounded-lg p-0.5 overflow-x-auto shrink min-w-0">
              {[{ id: 'calendar', icon: CalIcon, label: 'Calendario' }, ...(isAdmin ? [{ id: 'users', icon: Users, label: 'Utenti' }, { id: 'report', icon: BarChart3, label: 'Report' }] : [])].map(tab => (
                <button key={tab.id} onClick={() => setView(tab.id)} className={`px-2.5 sm:px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-medium whitespace-nowrap ${view === tab.id ? 'bg-white text-[#3e716c] shadow-sm' : 'text-white/80 hover:bg-white/10'}`} data-testid={`tab-${tab.id}`}>
                  <tab.icon size={13} className="inline mr-1 -mt-0.5" />{tab.label}
                </button>
              ))}
            </div>
            {canSync && <Button size="sm" variant="ghost" onClick={handleSync} disabled={syncing} className="text-white hover:bg-white/20 text-[11px] sm:text-xs whitespace-nowrap shrink-0 bg-white/10 rounded-lg" data-testid="sync-btn">
              <RefreshCw size={14} className={`mr-1 ${syncing ? 'animate-spin' : ''}`} /><span className="hidden sm:inline">{syncing ? 'Sync...' : 'Sincronizza iCal'}</span><span className="sm:hidden">{syncing ? '...' : 'Sync'}</span>
            </Button>}
          </div>
        </div>
      </div>
      <div className="h-[88px] sm:h-[80px]" />

      {/* Users */}
      {view === 'users' && isAdmin && (
        <div className="max-w-5xl mx-auto px-4 py-6">
          <h2 className="text-lg font-bold text-[#0A1312] mb-4"><Users size={18} className="inline text-[#65c3ba] mr-2" />Gestione Utenti</h2>
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-x-auto shadow-sm">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-[#F7F9F9] border-b"><tr>{['Nome', 'Email', 'Ruoli', 'Strutture'].map(h => <th key={h} className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
              <tbody>{allUsers.map(u => (<tr key={u.id} className="border-b hover:bg-[#F7F9F9]/50"><td className="px-4 py-3 font-medium">{u.name}</td><td className="px-4 py-3 text-xs text-[#0A1312]/60">{u.email}</td><td className="px-4 py-3">{u.roles?.map(r => <span key={r.role} className="bg-[#65c3ba]/10 text-[#3e716c] px-2 py-0.5 rounded text-xs mr-1">{r.role}</span>)}</td><td className="px-4 py-3 text-xs">{u.roles?.flatMap(r => r.properties || []).join(', ') || 'Tutte'}</td></tr>))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Report */}
      {view === 'report' && isAdmin && (
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#0A1312]"><BarChart3 size={18} className="inline text-[#65c3ba] mr-2" />Report Mensile</h2>
            <div className="flex items-center gap-3">
              <Select value={reportProp} onValueChange={setReportProp}>
                <SelectTrigger className="w-[160px] text-xs" data-testid="report-property-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le strutture</SelectItem>
                  {PROP_ORDER.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <MonthNav month={month} year={year} prevMonth={prevMonth} nextMonth={nextMonth} />
            </div>
          </div>
          {report.length > 0 && <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard label="Prenotazioni" value={report.reduce((s, r) => s + r.bookings, 0)} />
              <StatCard label="Check-out" value={report.reduce((s, r) => s + r.checkouts, 0)} color="text-red-500" />
              <StatCard label="Notti occupate" value={report.reduce((s, r) => s + r.occupied_nights, 0)} />
              <StatCard label="Occupazione" value={`${Math.round(report.reduce((s, r) => s + r.occupancy_rate, 0) / report.length)}%`} color="text-[#65c3ba]" />
            </div>
            {/* Excel export */}
            <div className="bg-white p-4 rounded-lg border border-[#E2E8F0] shadow-sm mb-4 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-[#0A1312]">Esporta pulizie:</span>
              <Select value={exportResp} onValueChange={setExportResp}>
                <SelectTrigger className="w-[130px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {RESPONSABILI.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleExportExcel} className="bg-[#65c3ba] text-white hover:bg-[#3e716c] text-xs" data-testid="export-tasks-btn">
                <Download size={14} className="mr-1" />Scarica Excel
              </Button>
            </div>
          </>}
        </div>
      )}

      {/* Calendar - Weekly Grid View */}
      {view === 'calendar' && (
        <div className="max-w-[1200px] mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-2 sm:gap-3 items-center mb-4">
            <Select value={selectedProp} onValueChange={setSelectedProp}>
              <SelectTrigger className="w-[160px]" data-testid="property-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le strutture</SelectItem>
                {PROP_ORDER.filter(n => properties.some(p => p.name === n)).map(n => <SelectItem key={n} value={n}><span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ background: PROP_COLORS[n]?.bg }} />{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <MonthNav month={month} year={year} prevMonth={prevMonth} nextMonth={nextMonth} />
            {lastSync?.timestamp && <span className="text-[10px] text-[#0A1312]/40 ml-auto">Sync: {new Date(lastSync.timestamp).toLocaleString('it-IT')}</span>}
          </div>

          {/* Calendar grid — 7 columns, weeks as rows */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm mb-4 overflow-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-[#E2E8F0] bg-[#F7F9F9]">
              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
                <div key={d} className={`text-center py-2 text-[10px] font-semibold text-[#0A1312]/50 uppercase tracking-wider ${d === 'Dom' ? 'text-red-400' : ''}`}>{d}</div>
              ))}
            </div>
            {/* Weeks */}
            <div>
              {(() => {
                const firstDay = new Date(year, month, 1).getDay();
                const offset = firstDay === 0 ? 6 : firstDay - 1;
                const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;
                const weeks = [];
                for (let w = 0; w < totalCells; w += 7) {
                  weeks.push(Array.from({ length: 7 }, (_, i) => {
                    const dayNum = w + i - offset + 1;
                    return dayNum >= 1 && dayNum <= daysInMonth ? dayNum : null;
                  }));
                }
                return weeks.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 border-b border-[#F0F0F0] last:border-b-0">
                    {week.map((day, di) => {
                      if (day === null) return <div key={di} className="min-h-[90px] bg-[#FAFAFA] border-r border-[#F0F0F0] last:border-r-0" />;
                      const dateStr = `${year}-${monthStr}-${String(day).padStart(2, '0')}`;
                      const d = new Date(year, month, day);
                      const isToday = d.toDateString() === new Date().toDateString();
                      const isSun = d.getDay() === 0;
                      const dayBookings = [];
                      visibleProps.forEach(propName => {
                        bookings.filter(b => b.property === propName).forEach(b => {
                          const isStart = b.start === dateStr;
                          const isEnd = b.end === dateStr;
                          const isStay = b.start < dateStr && b.end > dateStr;
                          if (isStart || isEnd || isStay) {
                            dayBookings.push({ ...b, isStart, isEnd, isStay, propName });
                          }
                        });
                      });
                      // Group: checkouts first, then stays, then checkins
                      const checkouts = dayBookings.filter(b => b.isEnd);
                      const stays = dayBookings.filter(b => b.isStay && !b.isEnd && !b.isStart);
                      const checkins = dayBookings.filter(b => b.isStart && !b.isEnd);
                      const allEvents = [...checkouts, ...stays, ...checkins];
                      return (
                        <div key={di} className={`min-h-[90px] p-1 border-r border-[#F0F0F0] last:border-r-0 ${isToday ? 'bg-[#65c3ba]/5' : ''} ${isSun ? 'bg-red-50/20' : ''}`}>
                          <div className={`text-[11px] mb-0.5 ${isToday ? 'bg-[#65c3ba] text-white w-5 h-5 rounded-full inline-flex items-center justify-center font-bold' : 'text-[#0A1312]/40 font-medium'}`}>{day}</div>
                          <div className="space-y-0.5">
                            {allEvents.slice(0, 5).map((ev, ei) => {
                              const colors = PROP_COLORS[ev.propName] || { bg: '#94a3b8' };
                              const srcLabel = SOURCE_LABELS[ev.source] || '';
                              if (ev.isEnd) {
                                return (
                                  <div key={`${ev.uid}-co-${ei}`} className="flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded text-white font-medium truncate" style={{ background: '#dc2626' }} title={`${isAdmin ? 'Check out' : 'Pulizia'}: ${ev.propName} — ${ev.summary}`}>
                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: colors.bg }} />
                                    <span className="truncate">{isAdmin ? 'Check out' : 'Pulizia'} {ev.propName}</span>
                                  </div>
                                );
                              }
                              if (ev.isStart) {
                                return (
                                  <div key={`${ev.uid}-ci-${ei}`} className="text-[9px] px-1 py-0.5 rounded text-white font-medium truncate flex items-center gap-0.5" style={{ background: colors.bg }} title={`Check-in: ${ev.propName} — ${ev.summary}`}>
                                    <span className="truncate">{ev.propName}</span>
                                    {isAdmin && srcLabel && <span className="text-[7px] bg-white/20 px-0.5 rounded ml-auto shrink-0">{srcLabel}</span>}
                                  </div>
                                );
                              }
                              return (
                                <div key={`${ev.uid}-st-${ei}`} className="text-[8px] px-1 py-0.5 rounded text-white truncate" style={{ background: colors.bg, opacity: 0.5 }} title={`${ev.propName}: soggiorno`}>
                                  {ev.propName}
                                </div>
                              );
                            })}
                            {allEvents.length > 5 && <div className="text-[8px] text-[#0A1312]/30">+{allEvents.length - 5}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Compact checkout list */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm">
            <div className="bg-[#3e716c] px-4 py-2 flex items-center gap-2 rounded-t-lg">
              <h3 className="font-semibold text-xs text-white">{isAdmin ? 'Check-out del mese' : 'Pulizie del mese'}</h3>
              <span className="ml-auto text-[10px] bg-white/20 px-1.5 py-0.5 rounded text-white">{opsTable.length}</span>
            </div>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              {opsTable.length === 0 ? (
                <p className="text-center text-[#0A1312]/40 py-8 text-xs">Sincronizza i calendari iCal</p>
              ) : (
                <table className="w-full text-xs" data-testid="ops-table">
                  <thead className="bg-[#F7F9F9] border-b sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold text-[9px] uppercase text-[#0A1312]/50">Data</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-[9px] uppercase text-[#0A1312]/50">Struttura</th>
                      {showGuestData && <th className="px-2 py-1.5 text-left font-semibold text-[9px] uppercase text-[#0A1312]/50">Ospite</th>}
                      {showGuestData && <th className="px-2 py-1.5 text-left font-semibold text-[9px] uppercase text-[#0A1312]/50">N. Ospiti</th>}
                      <th className="px-2 py-1.5 text-left font-semibold text-[9px] uppercase text-[#0A1312]/50">Conferma</th>
                      {canAssign && <th className="px-2 py-1.5 text-left font-semibold text-[9px] uppercase text-[#0A1312]/50">Responsabile</th>}
                      <th className="px-2 py-1.5 text-left font-semibold text-[9px] uppercase text-[#0A1312]/50">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opsTable.map((op, i) => {
                      const colors = PROP_COLORS[op.property] || {};
                      const isPast = op.date < new Date().toISOString().split('T')[0];
                      const assignees = getAssignees(op.property);
                      return (
                        <tr key={`${op.date}-${op.property}-${i}`} className={`border-b border-[#F0F0F0] hover:bg-[#F7F9F9]/50 ${isPast ? 'opacity-40' : ''} ${op.isHiddenTurnover ? 'bg-purple-50/50' : ''}`} data-testid={`ops-row-${i}`}>
                          <td className="px-2 py-1.5 font-medium text-[#0A1312] whitespace-nowrap">
                            {op.date.split('-').reverse().join('/')}
                            {op.isHiddenTurnover && <div className="text-[8px] text-purple-600 font-medium">da registrazione</div>}
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="inline-flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colors.bg }} />
                              <span className="truncate">{op.property}</span>
                            </span>
                            {op.isLongBlock && <div className="text-[8px] text-amber-600" title="Blocco lungo: potrebbe contenere più prenotazioni consecutive unite da Booking.com">possibile merge</div>}
                          </td>
                          {showGuestData && <td className="px-2 py-1.5 text-[#0A1312]/60 truncate max-w-[100px]">{op.summary?.replace('Reserved', '').replace('CLOSED - Not available', '').trim() || '—'}</td>}
                          {showGuestData && (
                            <td className="px-2 py-1.5 text-center">
                              {op.totalGuests !== null ? (
                                <span className="font-bold text-[#0A1312]">{op.totalGuests}</span>
                              ) : (
                                <span className="text-[#0A1312]/20">—</span>
                              )}
                              {op.dateMismatch && (
                                <div className="text-[8px] text-amber-600 leading-tight mt-0.5" title={`iCal: ${op.dateMismatch.ical_checkin}→${op.dateMismatch.ical_checkout} | Reg: ${op.dateMismatch.reg_checkin}→${op.dateMismatch.reg_checkout}`}>
                                  Date diverse
                                </div>
                              )}
                            </td>
                          )}
                          <td className="px-2 py-1.5">
                            {op.isBlock ? (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-500 border border-gray-200">Blocco</span>
                            ) : op.confirmed ? (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`confirmed-${i}`}>Confermato</span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid={`unconfirmed-${i}`} title={`${op.daysUntil} giorni al checkout`}>Non confermato</span>
                            )}
                          </td>
                          {canAssign && (
                            <td className="px-2 py-1.5">
                              {op.taskId ? (
                                <select value={op.assigned} onChange={e => updateTask(op.taskId, { assigned_to: e.target.value, status: e.target.value ? 'assegnata' : 'da_assegnare' })}
                                  className="border border-[#E2E8F0] rounded px-1 py-0.5 text-[10px] bg-white w-[100px]" data-testid={`assign-${i}`}>
                                  <option value="">—</option>
                                  {(userReassignTo
                                    ? RESPONSABILI.filter(r => userReassignTo.includes(r.id))
                                    : assignees
                                  ).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </select>
                              ) : <span className="text-[#0A1312]/20">—</span>}
                            </td>
                          )}
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium border ${
                              op.status === 'completata' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              op.status === 'assegnata' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              op.status === 'in_corso' ? 'bg-yellow-50 text-yellow-800 border-yellow-200' :
                              'bg-orange-50 text-orange-700 border-orange-200'
                            }`}>{STATUS_LABELS[op.status]}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CleaningDashboard;
